import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import contentToolsService from "../services/contentToolsService";
import {
  ActiveClient,
  BlueprintContentBundle,
} from "../types/contentTools";
import { useSelector } from "react-redux";
import { selectIsOperatorSession } from "../store/slices/authUserSlice";
import BeeEditor, { BeeEditorHandle } from "./BeeEditor";
import {
  unwrapRowsFromDesign,
  wrapRowsInDesign,
} from "../utils/beefreeRows";
import { ReactComponent as InfoIcon } from "../assets/icons/Info.svg";
import SectionsPanel from "./contentTools/SectionsPanel";
import CopyFromBlueprintPanel from "./contentTools/CopyFromBlueprintPanel";
import IngredientGroupsPanel from "./contentTools/IngredientGroupsPanel";
import SourcesPanel from "./contentTools/SourcesPanel";

// ---------------------------------------------------------------------------
// Client autocomplete
// ---------------------------------------------------------------------------

interface ClientAutocompleteProps {
  clients: ActiveClient[];
  loading: boolean;
  selected: ActiveClient | null;
  onSelect: (client: ActiveClient) => void;
}

const ClientAutocomplete: React.FC<ClientAutocompleteProps> = ({
  clients,
  loading,
  selected,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return clients.filter(
      (c) =>
        c.title.toLowerCase().includes(term) ||
        (c.customer_email || "").toLowerCase().includes(term),
    );
  }, [clients, searchTerm]);

  const buttonText = loading
    ? "Loading clients…"
    : selected
      ? `${selected.title}${selected.customer_email ? ` — ${selected.customer_email}` : ""}`
      : "Select a client…";

  return (
    <div ref={wrapRef} style={{ position: "relative", maxWidth: "480px" }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        style={{
          width: "100%",
          padding: "10px 12px",
          border: "1px solid #e2e8f0",
          borderRadius: "6px",
          backgroundColor: "#fff",
          cursor: loading ? "not-allowed" : "pointer",
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "6px",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {buttonText}
        </span>
        <span style={{ fontSize: "10px" }}>{isOpen ? "▲" : "▼"}</span>
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            backgroundColor: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
            maxHeight: "360px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ padding: "8px", borderBottom: "1px solid #e2e8f0" }}>
            <input
              ref={inputRef}
              type="text"
              placeholder="Search by name or email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "6px 10px",
                border: "1px solid #e2e8f0",
                borderRadius: "4px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ overflowY: "auto", padding: "8px" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "12px", textAlign: "center", color: "#718096" }}>
                No clients found
              </div>
            ) : (
              filtered.map((c) => {
                const isSelected = selected?.blueprint_id === c.blueprint_id;
                return (
                  <div
                    key={c.blueprint_id}
                    onClick={() => {
                      onSelect(c);
                      setIsOpen(false);
                      setSearchTerm("");
                    }}
                    style={{
                      padding: "8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#EBF4FF" : "transparent",
                    }}
                  >
                    <div style={{ fontSize: "14px", fontWeight: 500, color: "#2D3748" }}>
                      {c.title}
                    </div>
                    <div style={{ fontSize: "12px", color: "#718096" }}>
                      {c.customer_email} · {c.cadence_label}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-artifact editor (BeeFree + JSON + HTML preview)
// ---------------------------------------------------------------------------

interface ContentArtifact {
  id: string;
  label: string;
  kind: "row" | "design";
  stored: object | null;
}

interface ArtifactEditorProps {
  artifact: ContentArtifact;
  /** Page-styling shell used when wrapping bare rows for the editor. */
  shell: object | null;
  onPersist: (storedValue: object) => Promise<void>;
  /**
   * 0-based index of the blueprint's footer row within the design's `rows`
   * array. Sections are inserted BEFORE this row. Only set for the default
   * email template artifact; null/undefined hides the highlight control.
   */
  footerIndex?: number | null;
  /**
   * Persist a new footer index (or null to clear it). Its presence signals
   * "this is the default email template tab", which shows the footer editor.
   */
  onPersistFooterIndex?: (index: number | null) => Promise<void>;
}

const ArtifactEditor: React.FC<ArtifactEditorProps> = ({
  artifact,
  shell,
  onPersist,
  footerIndex,
  onPersistFooterIndex,
}) => {
  const editorRef = useRef<BeeEditorHandle>(null);
  const persistingRef = useRef(false);
  const skipNextPersistRef = useRef(false);
  const [rightView, setRightView] = useState<"preview" | "json">("preview");
  const [html, setHtml] = useState<string>("");
  const [storedJson, setStoredJson] = useState<object | null>(artifact.stored);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(
    null,
  );

  // Footer index (template tab only) — local draft + save.
  const [footerIndexDraft, setFooterIndexDraft] = useState<string>(
    footerIndex == null ? "" : String(footerIndex),
  );
  const [savingFooterIndex, setSavingFooterIndex] = useState(false);

  // Keep the draft in sync when the parent's saved value changes.
  useEffect(() => {
    setFooterIndexDraft(footerIndex == null ? "" : String(footerIndex));
  }, [footerIndex]);

  const handleSaveFooterIndex = async () => {
    if (!onPersistFooterIndex) return;
    const raw = footerIndexDraft.trim();
    const parsed = raw === "" ? null : Number(raw);
    if (raw !== "" && (!Number.isInteger(parsed as number) || (parsed as number) < 0)) {
      setMessage({ type: "error", text: "Footer index must be a whole number ≥ 0." });
      return;
    }
    setSavingFooterIndex(true);
    try {
      await onPersistFooterIndex(parsed as number | null);
      setMessage({
        type: "success",
        text: raw === "" ? "Footer index cleared." : "Footer index saved.",
      });
    } catch (e: any) {
      setMessage({ type: "error", text: e?.message || "Failed to save footer index." });
    } finally {
      setSavingFooterIndex(false);
    }
  };

  // The full design loaded into the editor (rows get wrapped in a page shell).
  const initialDesign = useMemo(
    () =>
      artifact.kind === "row"
        ? wrapRowsInDesign(artifact.stored, shell)
        : (artifact.stored as object) || wrapRowsInDesign(null, shell),
    [artifact, shell],
  );

  const toStoredValue = (design: object): object =>
    artifact.kind === "row" ? unwrapRowsFromDesign(design) : design;

  const handleEditorChange = (design: object) => {
    setStoredJson(toStoredValue(design));
  };

  // Single persist path. Guarded so the editor's own Save button and the
  // outer Save button can both trigger a save without double-writing.
  const persist = async (design: object, savedHtml: string) => {
    if (persistingRef.current) return;
    persistingRef.current = true;
    setSaving(true);
    setHtml(savedHtml);
    setStoredJson(toStoredValue(design));
    try {
      await onPersist(toStoredValue(design));
      setMessage({ type: "success", text: "Saved." });
    } catch (e: any) {
      setMessage({ type: "error", text: e?.message || "Failed to save." });
    } finally {
      setSaving(false);
      persistingRef.current = false;
    }
  };

  // Fires from the BeeFree editor's own Save button (its config onSave hook).
  const handleEditorSave = (design: object, savedHtml: string) => {
    setHtml(savedHtml);
    setStoredJson(toStoredValue(design));
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    persist(design, savedHtml);
  };

  // Populate the preview + JSON once the editor is ready (no persist).
  const handleReady = () => {
    skipNextPersistRef.current = true;
    editorRef.current?.save();
  };

  // Outer Save button: force a save and persist from the resolved result.
  const handleSaveClick = async () => {
    setMessage(null);
    const result = await editorRef.current?.save();
    if (result) persist(result.design, result.html);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button
          className="primary-button"
          onClick={handleSaveClick}
          disabled={saving}
          style={{ minWidth: "120px" }}
        >
          {saving ? "Saving…" : "Save"}
        </button>

        {onPersistFooterIndex && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <label
              htmlFor={`footer-index-${artifact.id}`}
              style={{ fontSize: "13px", fontWeight: 600, color: "#4A5568" }}
            >
              Footer index
            </label>
            <input
              id={`footer-index-${artifact.id}`}
              type="number"
              min={0}
              step={1}
              value={footerIndexDraft}
              onChange={(e) => setFooterIndexDraft(e.target.value)}
              style={{
                width: "72px",
                padding: "5px 8px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            <button
              type="button"
              className="tertiary-info-button"
              onClick={handleSaveFooterIndex}
              disabled={savingFooterIndex}
            >
              {savingFooterIndex ? "Saving…" : "Save"}
            </button>
          </div>
        )}

        {onPersistFooterIndex && (
          <div
            className="tooltip-container"
            style={{ position: "relative", display: "inline-block" }}
          >
            <button
              type="button"
              className="tertiary-info-button"
              onClick={() => editorRef.current?.highlightRow(footerIndex ?? -1)}
            >
              <span style={{ display: "flex", alignItems: "center" }}>
                Highlight footer row
                <InfoIcon style={{ marginLeft: "8px" }} />
              </span>
            </button>
            <div
              className="tooltip"
              style={{
                width: "260px",
                whiteSpace: "normal",
                textAlign: "left",
                fontWeight: 400,
                top: "calc(100% + 6px)",
                right: 0,
                left: "auto",
                transform: "none",
              }}
            >
              All sections will be inserted ABOVE (before) this row index.
              The highlighted footer row is 0-based: it stays at the bottom of
              the content area.
            </div>
          </div>
        )}

        {message && (
          <span
            style={{
              fontSize: "14px",
              color: message.type === "success" ? "#2F855A" : "#C53030",
            }}
          >
            {message.text}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {/* Editor */}
        <div
          style={{
            width: "100%",
            minWidth: 0,
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            overflow: "hidden",
            height: "640px",
          }}
        >
          <BeeEditor
            ref={editorRef}
            containerId={`content-tools-editor-${artifact.id}`}
            design={initialDesign}
            onChange={handleEditorChange}
            onSave={handleEditorSave}
            onReady={handleReady}
          />
        </div>

        {/* Preview / JSON */}
        <div
          style={{
            width: "100%",
            minWidth: 0,
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            display: "flex",
            flexDirection: "column",
            height: "640px",
          }}
        >
          <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0" }}>
            {(["preview", "json"] as const).map((view) => (
              <button
                key={view}
                type="button"
                onClick={() => setRightView(view)}
                style={{
                  flex: 1,
                  padding: "10px",
                  border: "none",
                  background: rightView === view ? "#EBF4FF" : "#fff",
                  color: rightView === view ? "#2B6CB0" : "#4A5568",
                  fontWeight: rightView === view ? 600 : 400,
                  cursor: "pointer",
                  fontSize: "13px",
                }}
              >
                {view === "preview" ? "Rendered HTML" : "Raw JSON"}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            {rightView === "preview" ? (
              html ? (
                <iframe
                  title="preview"
                  srcDoc={html}
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              ) : (
                <div
                  style={{
                    padding: "16px",
                    color: "#718096",
                    fontSize: "13px",
                  }}
                >
                  Preview will render once the editor finishes loading.
                </div>
              )
            ) : (
              <pre
                style={{
                  margin: 0,
                  padding: "12px",
                  height: "100%",
                  overflow: "auto",
                  fontSize: "12px",
                  lineHeight: 1.5,
                  background: "#1A202C",
                  color: "#E2E8F0",
                  whiteSpace: "pre",
                }}
              >
                {JSON.stringify(storedJson, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ContentTools: React.FC = () => {
  const [clients, setClients] = useState<ActiveClient[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<ActiveClient | null>(null);
  const [bundle, setBundle] = useState<BlueprintContentBundle | null>(null);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [loadError, setLoadError] = useState<string>("");
  const [activeTabId, setActiveTabId] = useState<string>("content_row");
  const isOperator = useSelector(selectIsOperatorSession);

  useEffect(() => {
    if (!isOperator) return;
    contentToolsService
      .getActiveClients()
      .then(setClients)
      .catch((e) => setLoadError(e?.message || "Failed to load clients."))
      .finally(() => setClientsLoading(false));
  }, [isOperator]);

  const handleSelectClient = (client: ActiveClient) => {
    setSelectedClient(client);
    setBundle(null);
    setLoadError("");
    setBundleLoading(true);
    setActiveTabId("content_row");
    contentToolsService
      .getBlueprintContentBundle(client.blueprint_id)
      .then(setBundle)
      .catch((e) => setLoadError(e?.message || "Failed to load blueprint content."))
      .finally(() => setBundleLoading(false));
  };

  // Build the tab list + per-artifact save handlers from the loaded bundle.
  const tabs: ContentArtifact[] = useMemo(() => {
    if (!bundle) return [];
    const base: ContentArtifact[] = [
      { id: "content_row", label: "Content Row", kind: "row", stored: bundle.content_row },
      { id: "link_dump_row", label: "Link Dump", kind: "row", stored: bundle.link_dump_row },
      {
        id: "default_manual_post_template",
        label: "Default Email Template",
        kind: "design",
        stored: bundle.default_manual_post_template,
      },
    ];
    const custom: ContentArtifact[] = bundle.custom_content_rows.map((c) => ({
      id: `custom:${c.id}`,
      label: c.title || "Custom Content",
      kind: "row",
      stored: c.beefree_template,
    }));
    return [...base, ...custom];
  }, [bundle]);

  const activeArtifact = tabs.find((t) => t.id === activeTabId) || null;

  const handlePersist = async (artifact: ContentArtifact, value: object) => {
    if (!bundle) return;
    if (artifact.id.startsWith("custom:")) {
      const customId = artifact.id.slice("custom:".length);
      await contentToolsService.updateCustomContentRow(customId, value);
      setBundle((prev) =>
        prev
          ? {
              ...prev,
              custom_content_rows: prev.custom_content_rows.map((c) =>
                c.id === customId ? { ...c, beefree_template: value } : c,
              ),
            }
          : prev,
      );
    } else {
      const field = artifact.id as
        | "content_row"
        | "link_dump_row"
        | "default_manual_post_template";
      await contentToolsService.updateBlueprintContent(
        bundle.blueprint_id,
        field,
        value,
      );
      setBundle((prev) => (prev ? { ...prev, [field]: value } : prev));
    }
  };

  const handlePersistFooterIndex = async (index: number | null) => {
    if (!bundle) return;
    await contentToolsService.updateBlueprintFooterIndex(bundle.blueprint_id, index);
    setBundle((prev) =>
      prev ? { ...prev, beefree_row_footer_index: index } : prev,
    );
  };

  // Re-fetch the bundle so read-only section fields (preferred_sources, linked
  // state) stay in sync after a section-config write.
  const refreshBundle = async () => {
    if (!bundle) return;
    const fresh = await contentToolsService.getBlueprintContentBundle(
      bundle.blueprint_id,
    );
    setBundle(fresh);
  };

  // Operator-only page: hide everything (and skip the client load above) for
  // non-operator sessions. The backend also enforces require_operator! on every
  // endpoint; this is defense-in-depth plus a clean UX.
  if (!isOperator) {
    return (
      <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
        <h1 style={{ fontSize: "1.6em", fontWeight: 700, marginBottom: "4px" }}>
          Content Tools
        </h1>
        <p style={{ color: "#718096", fontSize: "14px" }}>
          This page is available to Potions operators only.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      <h1 style={{ fontSize: "1.6em", fontWeight: 700, marginBottom: "4px" }}>
        Content Tools
      </h1>
      <p style={{ color: "#718096", marginBottom: "20px", fontSize: "14px" }}>
        Edit a client's content row, link dump, custom content, and default email
        template in the BeeFree editor. Changes save back to the blueprint.
      </p>

      <div style={{ marginBottom: "20px" }}>
        <label
          style={{
            display: "block",
            fontSize: "13px",
            fontWeight: 600,
            color: "#4A5568",
            marginBottom: "6px",
          }}
        >
          Client
        </label>
        <ClientAutocomplete
          clients={clients}
          loading={clientsLoading}
          selected={selectedClient}
          onSelect={handleSelectClient}
        />
      </div>

      {loadError && (
        <div style={{ color: "#C53030", fontSize: "14px", marginBottom: "16px" }}>
          {loadError}
        </div>
      )}

      {bundleLoading && (
        <div style={{ color: "#718096", fontSize: "14px" }}>Loading content…</div>
      )}

      {bundle && !bundleLoading && (
        <>
          <CopyFromBlueprintPanel
            clients={clients}
            currentBlueprintId={bundle.blueprint_id}
            onCopy={(sourceId, copyCustom) =>
              contentToolsService.copyFromBlueprint(
                bundle.blueprint_id,
                sourceId,
                copyCustom,
              )
            }
            onCopied={() => refreshBundle()}
          />

          {/* Tab bar */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              borderBottom: "1px solid #e2e8f0",
              marginBottom: "16px",
            }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTabId(tab.id)}
                style={{
                  padding: "10px 14px",
                  border: "none",
                  borderBottom:
                    activeTabId === tab.id
                      ? "2px solid #2B6CB0"
                      : "2px solid transparent",
                  background: "none",
                  color: activeTabId === tab.id ? "#2B6CB0" : "#4A5568",
                  fontWeight: activeTabId === tab.id ? 600 : 400,
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                {tab.label}
                {tab.stored == null && (
                  <span style={{ color: "#A0AEC0", marginLeft: "6px", fontSize: "11px" }}>
                    (empty)
                  </span>
                )}
              </button>
            ))}
          </div>

          {activeArtifact && (
            <ArtifactEditor
              // Remount the editor when the artifact or client changes.
              key={`${bundle.blueprint_id}:${activeArtifact.id}`}
              artifact={activeArtifact}
              shell={bundle.default_manual_post_template}
              footerIndex={
                activeArtifact.id === "default_manual_post_template"
                  ? bundle.beefree_row_footer_index
                  : null
              }
              onPersistFooterIndex={
                activeArtifact.id === "default_manual_post_template"
                  ? handlePersistFooterIndex
                  : undefined
              }
              onPersist={(value) => handlePersist(activeArtifact, value)}
            />
          )}

          <div
            style={{
              marginTop: "24px",
              borderTop: "1px solid #e2e8f0",
              paddingTop: "16px",
            }}
          >
            <h2 style={{ fontSize: "1.15em", fontWeight: 700, marginBottom: "12px" }}>
              Sections
            </h2>
            <SectionsPanel
              sections={bundle.sections}
              customContentRows={bundle.custom_content_rows}
              availableGroups={bundle.available_groups}
              onLinkTemplate={async (i, id) => {
                await contentToolsService.linkTemplateToSection(
                  bundle.blueprint_id,
                  i,
                  id,
                );
                await refreshBundle();
              }}
              onUpdateGroup={async (i, id) => {
                await contentToolsService.updateSectionGroup(
                  bundle.blueprint_id,
                  i,
                  id,
                );
                await refreshBundle();
              }}
              onUpdateConfig={async (i, cfg) => {
                await contentToolsService.updateSectionConfig(
                  bundle.blueprint_id,
                  i,
                  cfg,
                );
                await refreshBundle();
              }}
            />
          </div>

          <div
            style={{
              marginTop: "24px",
              borderTop: "1px solid #e2e8f0",
              paddingTop: "16px",
            }}
          >
            <h2 style={{ fontSize: "1.15em", fontWeight: 700, marginBottom: "12px" }}>
              Ingredient Groups
            </h2>
            <IngredientGroupsPanel
              groups={bundle.available_groups}
              customContentRows={bundle.custom_content_rows}
              onUpdatePreferredTemplate={async (groupId, templateId) => {
                await contentToolsService.updateIngredientGroup(
                  groupId,
                  templateId,
                );
                await refreshBundle();
              }}
            />
          </div>

          <div
            style={{
              marginTop: "24px",
              borderTop: "1px solid #e2e8f0",
              paddingTop: "16px",
            }}
          >
            <h2 style={{ fontSize: "1.15em", fontWeight: 700, marginBottom: "12px" }}>
              Sources
            </h2>
            <SourcesPanel
              sources={bundle.sources}
              sections={bundle.sections}
              onAdd={async (payload) => {
                await contentToolsService.createSource(bundle.blueprint_id, payload);
                await refreshBundle();
              }}
              onUpdate={async (sourceId, payload) => {
                await contentToolsService.updateSource(
                  bundle.blueprint_id,
                  sourceId,
                  payload,
                );
                await refreshBundle();
              }}
              onDelete={async (sourceId) => {
                await contentToolsService.deleteSource(
                  bundle.blueprint_id,
                  sourceId,
                );
                await refreshBundle();
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ContentTools;
